import { useCallback } from 'react'
import { CalendarIntegration } from 'types/calendarIntegrations'
import {
  Appointment,
  AppointmentCalendarIntegrationsRequest,
  AppointmentCancelRequest,
  AppointmentCreateRequest,
  AppointmentGetRequest,
  AppointmentRescheduleRequest,
  AppointmentsGetManyRequest,
  AppointmentsPageResponse,
  AppointmentTypes,
  AvailabilitiesResponse,
  AvailabilityConstraint,
  AvailabilityConstraintCreateRequest,
  AvailabilityConstraintDeleteRequest,
  AvailabilityConstraintDeleteResponse,
  AvailabilityConstraintHistoriesGetRequest,
  AvailabilityConstraintHistory,
  AvailabilityConstraintsGetRequest,
  AvailabilityConstraintUpdateRequest,
  GetCalendarIntegrationsSummariesQuery,
  GetCalendarIntegrationsSummariesResponse,
  Page,
  PaginatedResponse,
  Reservation,
  ReservationConvertRequest,
  ReservationCreateRequest,
  ReservationsGetManyRequest,
  Resource,
  Shop,
  Unavailabilities,
} from '../../../types'
import { TimeZones } from '../../../types/timeZone'
import { HttpClientConfig, useHttpClientConfig } from '../../useHttpClientConfig'
import { useNotification } from '../../useNotification'
import { useServiceCountryUrl } from '../useServiceCountryUrl'
import {
  makeCancelAppointment,
  makeCreateAppointment,
  makeGetAppointment,
  makeGetAppointments,
  makeRescheduleAppointment,
} from './appointments'
import { AppointmentTypeFilters, makeGetAppointmentTypes } from './appointmentTypes'
import { GetAvailabilitiesRequest, makeGetAvailabilities } from './availabilities'
import { AsegoDaysResponse, makeGetAvailableDays } from './availabilities/asego'
import { makeGetAsegoTimes } from './availabilities/asegoTimes'

import {
  makeCreateAvailabilityConstraint,
  makeDeleteAvailabilityConstraint,
  makeGetAvailabilityConstraintHistories,
  makeGetAvailabilityConstraints,
  makeUpdateAvailabilityConstraint,
} from './availabilityConstraints'
import { makeGetCalendarIntegrations } from './calendarIntegrations'
import { makeGetCalendarIntegrationsSummaries } from './calendarIntegrationsSummaries'
import {
  makeGetCalendarIntegrationAppointmentTypeMappings,
  CalendarIntegrationAppointmentTypeMappingsFilters,
} from './calendarIntegrationAppointmentTypeMappings'
import {
  makeCancelReservation,
  makeConvertReservation,
  makeCreateReservation,
  makeGetReservation,
  makeGetReservations,
} from './reservations'
import { makeGetResources, ResourcesFilters } from './resources'
import { makeGetShops, ShopsFilters } from './shops'
import { makeGetTimeZones, TimeZoneFilters } from './timeZones'
import { GetUnavailabilitiesRequest, makeGetUnavailabilities } from './unavailabilities'
import { makeGetResourceAppointmentTypes } from './resourceAppointmentTypes'
import { AppointmentType } from 'constants/appointmentType'
import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { makeGetAppointmentCalendarIntegrations } from './appointmentCalendarIntegrations'
import { AppointmentCalendarIntegration } from 'types/appointmentCalendarIntegration'
import { GetCalendarIntegrationAppointmentTypeMappingsResponse } from '../../../types/calendarIntegrationAppointmentTypeMapping'

interface AppointmentService {
  cancelAppointment: (request: AppointmentCancelRequest) => Promise<Appointment>
  cancelReservation: (id: string) => Promise<void>
  convertReservation: (id: string, request: ReservationConvertRequest) => Promise<void>
  createAppointment: (request: AppointmentCreateRequest) => Promise<Appointment>
  createAvailabilityConstraint: (resourceId: string, request: AvailabilityConstraintCreateRequest) => Promise<AvailabilityConstraint>
  createReservation: (request: ReservationCreateRequest) => Promise<Reservation>
  deleteAvailabilityConstraint: (request: AvailabilityConstraintDeleteRequest) => Promise<AvailabilityConstraintDeleteResponse>
  getAppointment: (request: AppointmentGetRequest) => Promise<Appointment | undefined>
  getAppointments: (request: AppointmentsGetManyRequest) => Promise<AppointmentsPageResponse>
  getAppointmentTypes: (filters: AppointmentTypeFilters) => Promise<AppointmentTypes>
  getAppointmentCalendarIntegrations: (request: AppointmentCalendarIntegrationsRequest) => Promise<AppointmentCalendarIntegration[]>
  getAsegoDays: (request: GetAvailabilitiesRequest) => Promise<AsegoDaysResponse>
  getAsegoTimes: (request: GetAvailabilitiesRequest) => Promise<AvailabilitiesResponse>
  getResourceAppointmentTypes: (resourceId: string) => Promise<Record<DeliveryModel, AppointmentType[]>>
  getAvailabilities: (request: GetAvailabilitiesRequest) => Promise<AvailabilitiesResponse>
  getAvailabilityConstraintHistories: (
    request: AvailabilityConstraintHistoriesGetRequest
  ) => Promise<PaginatedResponse<AvailabilityConstraintHistory>>
  getAvailabilityConstraints: (request: AvailabilityConstraintsGetRequest) => Promise<AvailabilityConstraint[]>
  getReservation: (id: string) => Promise<Reservation | undefined>
  getReservations: (filters: ReservationsGetManyRequest) => Promise<Page<Reservation>>
  getResources: (filters: ResourcesFilters) => Promise<Page<Resource>>
  getShops: (filters: ShopsFilters) => Promise<Page<Shop>>
  getTimeZones: (filters: TimeZoneFilters) => Promise<TimeZones>
  getUnavailabilities: (request: GetUnavailabilitiesRequest) => Promise<Unavailabilities>
  getCalendarIntegrations: (resourceId: string) => Promise<CalendarIntegration[]>
  getCalendarIntegrationsSummaries: (query: GetCalendarIntegrationsSummariesQuery) => Promise<GetCalendarIntegrationsSummariesResponse>
  getCalendarIntegrationAppointmentTypeMappings: (
    filters: CalendarIntegrationAppointmentTypeMappingsFilters
  ) => Promise<GetCalendarIntegrationAppointmentTypeMappingsResponse>
  isReady: boolean
  rescheduleAppointment: (request: AppointmentRescheduleRequest) => Promise<Appointment>
  updateAvailabilityConstraint: (
    resourceId: string,
    id: string,
    request: AvailabilityConstraintUpdateRequest
  ) => Promise<AvailabilityConstraint>
}

const useAppointmentsService = (): AppointmentService => {
  const { config } = useHttpClientConfig()
  const { notify } = useNotification()
  const { url } = useServiceCountryUrl('appointment')
  const baseUrl = url.href

  const cancelAppointment = useCallback(makeCancelAppointment(baseUrl, config as HttpClientConfig, notify), [baseUrl, config, notify])
  const cancelReservation = useCallback(makeCancelReservation(baseUrl, config as HttpClientConfig), [baseUrl, config, notify])
  const convertReservation = useCallback(makeConvertReservation(baseUrl, config as HttpClientConfig, notify), [baseUrl, config, notify])
  const createAppointment = useCallback(makeCreateAppointment(baseUrl, config as HttpClientConfig, notify), [baseUrl, config, notify])
  const createAvailabilityConstraint = useCallback(makeCreateAvailabilityConstraint(baseUrl, config as HttpClientConfig, notify), [
    baseUrl,
    config,
    notify,
  ])
  const createReservation = useCallback(makeCreateReservation(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const deleteAvailabilityConstraint = useCallback(makeDeleteAvailabilityConstraint(baseUrl, config as HttpClientConfig, notify), [
    baseUrl,
    config,
    notify,
  ])
  const getAppointment = useCallback(makeGetAppointment(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getAppointments = useCallback(makeGetAppointments(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getAppointmentTypes = useCallback(makeGetAppointmentTypes(baseUrl, config as HttpClientConfig), [baseUrl, config])

  const getAsegoDays = useCallback(makeGetAvailableDays(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getAsegoTimes = useCallback(makeGetAsegoTimes(baseUrl, config as HttpClientConfig), [baseUrl, config])

  const getResourceAppointmentTypes = useCallback(makeGetResourceAppointmentTypes(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getAvailabilities = useCallback(makeGetAvailabilities(baseUrl, config as HttpClientConfig), [baseUrl, config])

  const getAvailabilityConstraints = useCallback(makeGetAvailabilityConstraints(baseUrl, config as HttpClientConfig, notify), [
    baseUrl,
    config,
    notify,
  ])

  const getAvailabilityConstraintHistories = useCallback(
    makeGetAvailabilityConstraintHistories(baseUrl, config as HttpClientConfig, notify),
    [baseUrl, config, notify]
  )

  const getReservation = useCallback(makeGetReservation(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getReservations = useCallback(makeGetReservations(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getResources = useCallback(makeGetResources(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getShops = useCallback(makeGetShops(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getTimeZones = useCallback(makeGetTimeZones(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getUnavailabilities = useCallback(makeGetUnavailabilities(baseUrl, config as HttpClientConfig, notify), [baseUrl, config, notify])
  const getCalendarIntegrations = useCallback(makeGetCalendarIntegrations(baseUrl, config as HttpClientConfig), [baseUrl, config])
  const getCalendarIntegrationsSummaries = useCallback(makeGetCalendarIntegrationsSummaries(baseUrl, config as HttpClientConfig), [
    baseUrl,
    config,
  ])
  const getAppointmentCalendarIntegrations = useCallback(makeGetAppointmentCalendarIntegrations(baseUrl, config as HttpClientConfig), [
    baseUrl,
    config,
  ])
  const getCalendarIntegrationAppointmentTypeMappings = useCallback(
    makeGetCalendarIntegrationAppointmentTypeMappings(baseUrl, config as HttpClientConfig),
    [baseUrl, config]
  )

  const rescheduleAppointment = useCallback(makeRescheduleAppointment(baseUrl, config as HttpClientConfig, notify), [
    baseUrl,
    config,
    notify,
  ])

  const updateAvailabilityConstraint = useCallback(makeUpdateAvailabilityConstraint(baseUrl, config as HttpClientConfig, notify), [
    baseUrl,
    config,
    notify,
  ])

  return {
    cancelAppointment,
    cancelReservation,
    convertReservation,
    createAppointment,
    createAvailabilityConstraint,
    createReservation,
    deleteAvailabilityConstraint,
    getAppointment,
    getAppointments,
    getAppointmentTypes,
    getAppointmentCalendarIntegrations,
    getAsegoDays,
    getAsegoTimes,
    getResourceAppointmentTypes,
    getAvailabilities,
    getAvailabilityConstraintHistories,
    getAvailabilityConstraints,
    getReservation,
    getReservations,
    getResources,
    getShops,
    getTimeZones,
    getUnavailabilities,
    getCalendarIntegrations,
    getCalendarIntegrationsSummaries,
    getCalendarIntegrationAppointmentTypeMappings,
    isReady: Boolean(config),
    rescheduleAppointment,
    updateAvailabilityConstraint,
  }
}

export { useAppointmentsService }
